// Parser for Facebook (Meta) warrant returns: the records.html file Meta sends, made of
// <div id="property-…"> sections. Plain JavaScript, no React or DOM: used by the Facebook
// Warrant Return Viewer page and by the Case Analyzer desktop app.

// ─── Section metadata ────────────────────────────────────────────────────────

export const SECTION_KEYS = [
  'request_parameters','ncmec_reports','name','alternate_name',
  'name_changes','emails','email_changes','vanity','vanity_changes',
  'registration_date','registration_ip','account_end_date',
  'account_status_history','address','current_city','phone_numbers',
  'removed_phone_numbers','credit_cards','paypal_accounts','direct_debit',
  'ip_addresses','active_sessions','password_changes','applications',
  'administrative_records','notification_settings','privacy_settings',
  'gender','date_of_birth','screen_names','groups','removed_friends',
  'friends','friend_requests','checkins','last_location','wallposts',
  'status_updates','shares','photos','security_logs','unified_messages',
  'secret_conversations','linked_accounts','reported_conversations',
  'reported_disappearing_messages','messenger_day','location_services',
  'posts_to_other_walls','encrypted_connection_info','encrypted_groups_info',
  'additional_profiles','consented_messenger_data',
];

export const SECTION_LABELS = Object.fromEntries(
  SECTION_KEYS.map(k => [k, k.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())])
);

export const MESSAGE_SECTION_KEYS = [
  'unified_messages','secret_conversations','reported_conversations',
  'reported_disappearing_messages','consented_messenger_data',
];
export const MESSAGE_SECTIONS = new Set(MESSAGE_SECTION_KEYS);
export const IP_KEYS = new Set(['ip_addresses','active_sessions','security_logs']);
export const POSTS_SECTION_KEYS = new Set(['wallposts','status_updates','shares']);
export const FB_MEDIA_SECTION_KEYS = new Set(['photos']);

// ─── Parser ──────────────────────────────────────────────────────────────────

function htmlClean(raw) {
  return raw
    .replace(/<[^>]+>/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&nbsp;/g, ' ')
    .replace(/&#(\d+);/g, (_, n) => String.fromCharCode(+n))
    .replace(/\s+/g, ' ')
    .trim();
}

function sliceSections(html) {
  const re = /<div\s+id="(property-[^"]+)"/g;
  const matches = [];
  let m;
  while ((m = re.exec(html)) !== null) {
    matches.push({ key: m[1].replace('property-', ''), index: m.index });
  }
  const out = {};
  for (let i = 0; i < matches.length; i++) {
    const { key, index } = matches[i];
    const end = i + 1 < matches.length ? matches[i + 1].index : html.length;
    out[key] = html.slice(index, end);
  }
  return out;
}

function fieldValue(html, label) {
  const target = label.toLowerCase();
  const re = />([A-Za-z][^<\n]{1,80}?)<div class="m"><div>([^<]+)/g;
  let m;
  while ((m = re.exec(html)) !== null) {
    if (m[1].trim().toLowerCase() === target) {
      const v = htmlClean(m[2]);
      if (v && !v.toLowerCase().includes('no responsive records located')) return v;
    }
  }
  return '';
}

function extractName(html) {
  return [fieldValue(html, 'first'), fieldValue(html, 'middle'), fieldValue(html, 'last')]
    .filter(Boolean).join(' ');
}

const COUNT_HINTS = {
  ip_addresses: '>IP Address<', active_sessions: '>IP Address<',
  security_logs: '>IP Address<', unified_messages: '>Thread<',
  secret_conversations: 'Thread<', friends: '>Name<',
  removed_friends: '>Name<', checkins: '>Name<',
  photos: '<img ',
};

function countSection(key, html) {
  const hint = COUNT_HINTS[key];
  if (hint) {
    let count = 0, pos = 0;
    while ((pos = html.indexOf(hint, pos)) !== -1) { count++; pos += hint.length; }
    return count;
  }
  const re = />([A-Za-z][^<\n]{1,80}?)<div class="m"><div>([^<]+)/g;
  let m;
  while ((m = re.exec(html)) !== null) {
    const lbl = m[1].trim();
    if (lbl.endsWith('Definition')) continue;
    const val = htmlClean(m[2]);
    if (val && !val.toLowerCase().includes('no responsive records located')) return 1;
  }
  return 0;
}

function extractKvPairs(html) {
  const re = />([A-Za-z][^<\n]{1,80}?)<div class="m"><div>([^<]+)/g;
  const out = [];
  let m;
  while ((m = re.exec(html)) !== null) {
    const lbl = m[1].trim();
    if (!lbl || lbl.endsWith('Definition')) continue;
    const val = htmlClean(m[2]);
    if (!val || val.toLowerCase().includes('no responsive records located')) continue;
    out.push({ label: lbl, value: val });
  }
  const seen = new Set();
  let record = 0;
  for (const row of out) {
    if (seen.has(row.label)) { record++; seen.clear(); }
    seen.add(row.label);
    row.record = record;
  }
  return out;
}

function extractIpRecords(html) {
  // Layout 1: ip_addresses / security_logs — IP Address → Time → Action [→ Agent String]
  const re1 = />IP Address<div class="m"><div>([^<]+)/g;
  const positions = [];
  let m;
  while ((m = re1.exec(html)) !== null) {
    positions.push({ ip: m[1].trim(), index: m.index, end: m.index + m[0].length });
  }
  if (positions.length > 0) {
    const records = [];
    for (let i = 0; i < positions.length; i++) {
      const block = html.slice(positions[i].end,
        i + 1 < positions.length ? positions[i + 1].index : html.length);
      const timeM   = block.match(/>Time<div class="m"><div>([^<]+)/);
      const actionM = block.match(/>Action<div class="m"><div>([^<]+)/);
      if (!timeM || !actionM) continue;
      const agentM = block.match(/>Agent String<div class="m"><div>([^<]+)/);
      records.push({
        ip:     positions[i].ip,
        time:   timeM[1].trim(),
        action: actionM[1].trim(),
        agent:  agentM ? agentM[1].trim() : '',
      });
    }
    if (records.length > 0) return records;
  }
  // Layout 2: active_sessions — Date Created → Last Seen → IP Address [→ Device and Browser]
  const re2 = />Date Created<div class="m"><div>([^<]+)/g;
  const dcPos = [];
  while ((m = re2.exec(html)) !== null) {
    dcPos.push({ dateCreated: m[1].trim(), index: m.index, end: m.index + m[0].length });
  }
  return dcPos.map((dc, i) => {
    const block    = html.slice(dc.end, i + 1 < dcPos.length ? dcPos[i + 1].index : html.length);
    const lastSeenM = block.match(/>Last Seen<div class="m"><div>([^<]+)/);
    const ipM       = block.match(/>IP Address<div class="m"><div>([^<]+)/);
    const deviceM   = block.match(/>Device and Browser<div class="m"><div>([^<]+)/);
    return {
      ip:     ipM       ? ipM[1].trim()       : '',
      time:   lastSeenM ? lastSeenM[1].trim() : '',
      action: 'Created ' + dc.dateCreated,
      agent:  deviceM   ? deviceM[1].trim()   : '',
    };
  }).filter(r => r.ip);
}

function extractPhotos(html) {
  const re = /<img\s+src="([^"]+)"/g;
  const photos = [];
  let m;
  while ((m = re.exec(html)) !== null) {
    const src = m[1];
    const afterImg = m.index + m[0].length;
    const nextImg  = html.indexOf('<img ', afterImg);
    const blockEnd = nextImg === -1 ? html.length : nextImg;
    const block    = html.slice(m.index, blockEnd);
    photos.push({
      src,
      taken:    block.match(/>Taken<div class="m"><div>([^<]+)/)?.[1]?.trim()           || '',
      id:       block.match(/>Id<div class="m"><div>([^<]+)/)?.[1]?.trim()              || '',
      status:   block.match(/>Status<div class="m"><div>([^<]+)/)?.[1]?.trim()          || '',
      privacy:  block.match(/>Privacy Setting<div class="m"><div>([^<]+)/)?.[1]?.trim() || '',
      url:      block.match(/>Url<div class="m"><div>([^<]+)/)?.[1]?.trim()             || '',
      uploadIp: block.match(/>Upload Ip<div class="m"><div>([^<]+)/)?.[1]?.trim()       || '',
    });
  }
  return photos;
}

function parseParticipants(chunk) {
  const m = chunk.match(
    /Current Participants<div class="m"><div>([\s\S]*?)(?=<div class="t [oi]">(?!<div class="m">)|<div id="page_)/
  );
  if (!m) return [];
  return m[1].split(/<div class="p">/).map(htmlClean).filter(p => {
    if (!p) return false;
    if (/^\d{4}-\d{2}-\d{2}/.test(p)) return false;
    if (p === 'Group Profile Picture' || p === 'Linked Media File') return false;
    return true;
  });
}

function parseThreadMessages(chunk) {
  const re = />(Author|Sent|Body|Removed by Sender)<div class="m"><div>([\s\S]*?)(?=<div class="p">)/g;
  const messages = [];
  let current = null;
  let m;
  while ((m = re.exec(chunk)) !== null) {
    const label = m[1];
    const value = htmlClean(m[2]);
    if (label === 'Author') {
      if (current) messages.push(current);
      current = { author: value, sent: '', body: '', removed: false };
    } else if (current) {
      if (label === 'Sent')               current.sent    = value;
      else if (label === 'Body')          current.body    = value;
      else if (label === 'Removed by Sender') current.removed = true;
    }
  }
  if (current) messages.push(current);
  return messages;
}

function parseMessageThreads(html) {
  const re = /<div class="t i">Thread<div class="m"><div>\s*\((\d+)\)/g;
  const markers = [];
  let m;
  while ((m = re.exec(html)) !== null) markers.push({ tid: m[1], index: m.index });
  if (!markers.length) return [];
  return markers.map((marker, i) => {
    const chunk  = html.slice(marker.index, i + 1 < markers.length ? markers[i + 1].index : html.length);
    const nameM  = chunk.match(/Thread Name<div class="m"><div>([^<]+)/);
    return {
      thread_id:    marker.tid,
      thread_name:  nameM ? nameM[1].trim() : '',
      participants: parseParticipants(chunk),
      messages:     parseThreadMessages(chunk),
    };
  });
}

export function parseFacebookReturn(html) {
  const raw = sliceSections(html);
  const sections = {};
  for (const key of SECTION_KEYS) {
    const chunk = raw[key] || '';
    const count = countSection(key, chunk);
    sections[key] = {
      label:              SECTION_LABELS[key],
      count,
      is_message_section: MESSAGE_SECTIONS.has(key),
      kv_pairs: (count > 0 && !MESSAGE_SECTIONS.has(key) && !IP_KEYS.has(key) && !FB_MEDIA_SECTION_KEYS.has(key))
        ? extractKvPairs(chunk) : [],
    };
  }

  const summary = {
    name:              extractName(raw['name'] || ''),
    alternate_name:    fieldValue(raw['alternate_name'] || '', 'Alternate Name'),
    date_of_birth:     fieldValue(raw['date_of_birth'] || '', 'Date Of Birth'),
    gender:            fieldValue(raw['gender'] || '', 'Gender'),
    registration_date: fieldValue(raw['registration_date'] || '', 'Registration Date'),
    registration_ip:   fieldValue(raw['registration_ip'] || '', 'Registration Ip'),
    phone_numbers:     fieldValue(raw['phone_numbers'] || '', 'Phone Numbers'),
    emails:            fieldValue(raw['emails'] || '', 'Registered Email Addresses'),
    current_city:      fieldValue(raw['current_city'] || '', 'Current City'),
    address:           fieldValue(raw['address'] || '', 'Address'),
    account_end_date:  fieldValue(raw['account_end_date'] || '', 'Account End Date'),
  };

  const subject_id = fieldValue(raw['request_parameters'] || '', 'Account Identifier');

  const message_threads = {};
  for (const key of MESSAGE_SECTION_KEYS) {
    if (sections[key].count > 0) message_threads[key] = parseMessageThreads(raw[key] || '');
  }

  const ip_records = {};
  for (const key of ['ip_addresses','active_sessions','security_logs']) {
    if (sections[key].count > 0) ip_records[key] = extractIpRecords(raw[key] || '');
  }

  const ipCounts = {};
  for (const recs of Object.values(ip_records))
    for (const r of recs)
      if (r.ip.trim()) ipCounts[r.ip] = (ipCounts[r.ip] || 0) + 1;
  const top_ips = Object.entries(ipCounts)
    .sort((a, b) => b[1] - a[1]).slice(0, 10)
    .map(([ip, count]) => ({ ip, count }));

  const photos = extractPhotos(raw['photos'] || '');
  sections['photos'].count = photos.length;

  return { subject_id, summary, sections, message_threads, ip_records, top_ips, photos };
}
